import { useEffect, useMemo } from "react";
import { CanvasTexture, Vector2 } from "three";
import { StickerConfig } from "@/lib/sticker";
import { ModelConfig } from "@/lib/models";
import { calculateStickerSizeTexture } from "@/lib/sizeCalculator";
import { TEXTURE_SIZE } from "@/lib/constants";

function drawCheckerBackground(ctx: CanvasRenderingContext2D, grids = 48) {
  const size = TEXTURE_SIZE / grids;

  const patternCanvas = document.createElement("canvas");
  patternCanvas.width = patternCanvas.height = size * 2;

  const pctx = patternCanvas.getContext("2d");
  if (!pctx) return;

  // 填充白底
  pctx.fillStyle = "white";
  pctx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

  // 画灰色小格子
  pctx.fillStyle = "#f0f0f0";
  pctx.fillRect(0, 0, size, size);
  pctx.fillRect(size, size, size, size);

  const pattern = ctx.createPattern(patternCanvas, "repeat");
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  }
}

export const usePaintableTexture = (
  uvPoint: Vector2 | null,
  stickerConfig: StickerConfig,
  modelConfig: ModelConfig,
) => {
  // 使用 useMemo 来确保 canvas 和 texture 只被创建一次
  const paintableTexture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = TEXTURE_SIZE;

    const texture = new CanvasTexture(canvas);
    return texture;
  }, []);

  useEffect(() => {
    const canvas = paintableTexture.image as HTMLCanvasElement;

    console.log("Mounting canvas");

    canvas.style.width = "100%";
    canvas.style.height = "100%";

    const canvasContainer = document.querySelector("#canvas-container");
    if (canvasContainer) {
      canvasContainer.appendChild(canvas);
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (ctx) {
      drawCheckerBackground(ctx);
    }
  }, [paintableTexture]);

  useEffect(() => {
    // 只有在 uvPoint 和贴纸图片都存在时才进行绘制
    if (!uvPoint || !stickerConfig.image || !paintableTexture.image) return;

    const canvas = paintableTexture.image as HTMLCanvasElement;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // UV 坐标是归一化的 (0-1)，需要转换成 canvas 的像素坐标
    // 注意 Three.js 的 UV 原点在左下角，而 canvas 的原点在左上角，所以 y 轴需要翻转
    const x = uvPoint.x * canvas.width;
    const y = (1 - uvPoint.y) * canvas.height;

    // 在 React 应用中，图片资源通常在 public 目录下
    // 我们假设 stickerConfig.image 是一个有效的图片 URL
    const img = new Image();
    img.crossOrigin = "anonymous"; // 如果图片跨域，需要设置此项
    img.src = stickerConfig.image;

    img.onload = () => {
      // 1. 在绘制新图之前，先清空整个画布
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 2. （可选，但推荐）重新绘制背景色，以防画布变透明
      drawCheckerBackground(ctx);

      // 动态计算贴纸在 canvas 上的尺寸
      // 使用新的统一尺寸计算系统
      const stickerSizeTexture = calculateStickerSizeTexture(
        stickerConfig,
        modelConfig,
        TEXTURE_SIZE,
      );
      let stickerWidth = stickerSizeTexture.width;
      let stickerHeight = stickerSizeTexture.height;

      // 对于圆形贴纸，使用radius配置
      if (stickerConfig.shape === "circle") {
        const radius = stickerSizeTexture.radius;
        stickerWidth = radius * 2;
        stickerHeight = radius * 2;
      }

      const drawX = x - stickerWidth / 2;
      const drawY = y - stickerHeight / 2;

      // --- 关键改动在这里 ---
      // 3. 定义九个位置的偏移量
      const offsets = [
        [0, 0], // 中心
        [canvas.width, 0], // 右
        [-canvas.width, 0], // 左
        [0, canvas.height], // 下
        [0, -canvas.height], // 上
        [canvas.width, canvas.height], // 右下
        [canvas.width, -canvas.height], // 右上
        [-canvas.width, canvas.height], // 左下
        [-canvas.width, -canvas.height], // 左上
      ];

      // 4. 根据shape配置绘制不同形状的贴纸
      offsets.forEach(([offsetX, offsetY]) => {
        const finalX = drawX + offsetX;
        const finalY = drawY + offsetY;

        if (stickerConfig.shape === "circle") {
          // 圆形贴纸
          const radius = Math.min(stickerWidth, stickerHeight) / 2;
          const centerX = finalX + stickerWidth / 2;
          const centerY = finalY + stickerHeight / 2;

          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.clip();
          ctx.drawImage(img, finalX, finalY, stickerWidth, stickerHeight);
          ctx.restore();
        } else if (stickerConfig.shape === "contour") {
          // 轮廓贴纸 - 使用globalCompositeOperation来实现轮廓效果
          ctx.save();
          ctx.globalCompositeOperation = "source-over";
          ctx.drawImage(img, finalX, finalY, stickerWidth, stickerHeight);
          ctx.restore();
        } else {
          // 矩形贴纸（默认）
          ctx.drawImage(img, finalX, finalY, stickerWidth, stickerHeight);
        }
      });
      // --------------------

      // !! 非常重要：通知 Three.js 纹理内容已经更新
      paintableTexture.needsUpdate = true;
    };

    img.onerror = () => {
      console.error("贴纸图片加载失败:", stickerConfig.image);
    };
  }, [
    uvPoint,
    stickerConfig.image,
    stickerConfig.size,
    stickerConfig.unit,
    stickerConfig.shape,
    modelConfig,
    paintableTexture,
  ]); // 依赖项

  return paintableTexture;
};
